import React, { useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { KPICard } from '../../components/ui/KPICard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Database, Instagram, Facebook, Youtube, Twitter, Search, Filter, RefreshCw, Plus, Upload, BarChart2, ExternalLink, Video, Download } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
// Mock Data
interface AssetItem {
  id: string;
  platform: 'instagram' | 'facebook' | 'twitter' | 'youtube' | 'tiktok';
  handle: string;
  owner: string;
  pic: string;
  lastActivity: string;
  status: 'active' | 'inactive' | 'review';
  followers: number;
  engagement: string;
}
const mockAssets: AssetItem[] = [{
  id: '1',
  platform: 'instagram',
  handle: '@dlh.jakarta',
  owner: 'DLH DKI Jakarta',
  pic: 'Budi Santoso',
  lastActivity: '2 jam lalu',
  status: 'active',
  followers: 45200,
  engagement: '4.5%'
}, {
  id: '5',
  platform: 'tiktok',
  handle: '@bebassampah.id',
  owner: 'Komunitas Bebas Sampah',
  pic: 'Dina Marlina',
  lastActivity: '5 jam lalu',
  status: 'review',
  followers: 25000,
  engagement: '8.5%'
}];
export function AssetRegistryPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetItem | null>(null);
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <Instagram className="h-5 w-5 text-pink-600" data-id="element-672" />;
      case 'facebook':
        return <Facebook className="h-5 w-5 text-blue-600" data-id="element-673" />;
      case 'twitter':
        return <Twitter className="h-5 w-5 text-sky-500" data-id="element-674" />;
      case 'youtube':
        return <Youtube className="h-5 w-5 text-red-600" data-id="element-675" />;
      case 'tiktok':
        return <Video className="h-5 w-5 text-black" data-id="element-676" />;
      // Using Video icon as proxy for TikTok
      default:
        return <Database className="h-5 w-5 text-gray-500" data-id="element-677" />;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="hijau" data-id="element-678">Aktif</StatusBadge>;
      case 'inactive':
        return <StatusBadge status="merah" data-id="element-679">Tidak Aktif</StatusBadge>;
      case 'review':
        return <StatusBadge status="kuning" data-id="element-680">Perlu Review</StatusBadge>;
      default:
        return null;
    }
  };
  const columns = [{
    header: 'Platform',
    cell: (item: AssetItem) => <div className="flex items-center gap-3" data-id="element-681">
          <div className="p-2 bg-gray-50 rounded-full" data-id="element-682">
            {getPlatformIcon(item.platform)}
          </div>
          <span className="capitalize text-gray-700" data-id="element-683">{item.platform}</span>
        </div>
  }, {
    header: 'Akun / Handle',
    accessorKey: 'handle' as keyof AssetItem,
    cell: (item: AssetItem) => <span className="font-medium text-gray-900" data-id="element-684">{item.handle}</span>
  }, {
    header: 'Pemilik / Unit',
    accessorKey: 'owner' as keyof AssetItem,
    cell: (item: AssetItem) => <span className="text-gray-600" data-id="element-685">{item.owner}</span>
  }, {
    header: 'Followers',
    accessorKey: 'followers' as keyof AssetItem,
    cell: (item: AssetItem) => <span className="text-gray-600" data-id="element-686">{item.followers.toLocaleString()}</span>
  }, {
    header: 'Status',
    cell: (item: AssetItem) => getStatusBadge(item.status)
  }, {
    header: 'Aksi',
    cell: (item: AssetItem) => <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700" onClick={() => setSelectedAsset(item)} data-id="element-687">
          Edit
        </Button>
  }];
  return <CommandCenterLayout title="Asset Registry" subtitle="Database akun resmi dan aset digital pemerintah & mitra" data-id="element-688">
      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-689">
        <KPICard title="Total Akun" value="1,245" icon={Database} data-id="element-690" />
        <KPICard title="Akun Aktif" value="982" icon={RefreshCw} data-id="element-691" />
        <KPICard title="Total Audience" value="4.2M" icon={BarChart2} data-id="element-692" />
      </div>

      {/* Main Content */}
      <Card data-id="element-693">
        <CardHeader className="flex flex-row items-center justify-between pb-4" data-id="element-694">
          <div data-id="element-695">
            <CardTitle data-id="element-696">Daftar Aset Digital</CardTitle>
            <CardDescription data-id="element-697">
              Monitoring keaktifan kanal komunikasi
            </CardDescription>
          </div>
          <div className="flex gap-2" data-id="element-698">
            <Button variant="outline" className="gap-2" onClick={() => alert('Exporting CSV...')} data-id="element-699">
              <Download className="h-4 w-4" data-id="element-700" />
              Export CSV
            </Button>
            <Button className="bg-primary-600 hover:bg-primary-700 text-white gap-2" onClick={() => {
            setSelectedAsset(null);
            setIsCreateModalOpen(true);
          }} data-id="element-701">
              <Plus className="h-4 w-4" data-id="element-702" />
              Tambah Aset
            </Button>
          </div>
        </CardHeader>
        <CardContent data-id="element-703">
          <DataTable data={mockAssets} columns={columns} keyExtractor={item => item.id} data-id="element-704" />
        </CardContent>
      </Card>

      {/* Create/Edit Asset Modal */}
      <Modal isOpen={isCreateModalOpen || !!selectedAsset} onClose={() => {
      setIsCreateModalOpen(false);
      setSelectedAsset(null);
    }} title={selectedAsset ? 'Edit Aset Digital' : 'Tambah Aset Baru'} size="lg" footer={<div className="flex justify-end gap-2 w-full" data-id="element-706">
            <Button variant="outline" onClick={() => {
        setIsCreateModalOpen(false);
        setSelectedAsset(null);
      }} data-id="element-707">
              Batal
            </Button>
            <Button className="bg-primary-600 text-white" data-id="element-708">Simpan Data</Button>
          </div>} data-id="element-705">
        <div className="space-y-4" data-id="element-709">
          <div className="grid grid-cols-2 gap-4" data-id="element-710">
            <div data-id="element-711">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-712">
                Platform
              </label>
              <Select defaultValue={selectedAsset?.platform || 'instagram'} data-id="element-713">
                <option value="instagram" data-id="element-714">Instagram</option>
                <option value="facebook" data-id="element-715">Facebook</option>
                <option value="tiktok" data-id="element-716">TikTok</option>
              </Select>
            </div>
            <div data-id="element-717">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-718">
                Handle
              </label>
              <Input defaultValue={selectedAsset?.handle} data-id="element-719" />
            </div>
            <div className="col-span-2" data-id="element-720">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-721">
                Catatan Insiden
              </label>
              <Textarea placeholder="Catat jika ada insiden keamanan atau isu konten..." data-id="element-722" />
            </div>
          </div>
        </div>
      </Modal>
    </CommandCenterLayout>;
}